import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, CheckCircle2, Landmark, Clock, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

export default function ManualPaymentModal({ planId, onClose }) {
  const [order, setOrder] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sender, setSender] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!planId) return;
    setOrder(null); setDone(false); setSender(""); setNote("");
    setLoading(true);
    api.post("/manual/order", { package_id: planId })
      .then((r) => { setOrder(r.data.order); setBank(r.data.bank); })
      .catch(() => { toast.error("Gagal membuat pesanan."); onClose(); })
      .finally(() => setLoading(false));
  }, [planId]);

  const copy = (txt, label) => { navigator.clipboard?.writeText(String(txt)); toast.success(`${label} disalin`); };

  const submit = async () => {
    if (!sender.trim()) { toast.error("Isi nama pengirim transfer."); return; }
    setSubmitting(true);
    try {
      await api.post("/manual/confirm", { order_id: order.order_id, sender_name: sender, note });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim konfirmasi.");
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      {planId && (
        <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto p-3 sm:p-6 bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="manual-payment-modal">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="card w-full max-w-lg my-4 p-0 overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
              <span className="inline-flex items-center gap-2 font-heading font-bold">
                <Landmark size={18} className="text-accent" /> Pembayaran via Transfer BRI
              </span>
              <button onClick={onClose} data-testid="manual-close" className="p-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"><X size={18} /></button>
            </div>

            {loading || !order ? (
              <div className="py-16 text-center"><Loader2 size={26} className="animate-spin text-accent mx-auto mb-2" /><p className="text-sm text-[var(--text-secondary)]">Menyiapkan pesanan…</p></div>
            ) : done ? (
              <div className="p-8 text-center" data-testid="manual-success">
                <CheckCircle2 size={44} className="text-bull mx-auto mb-3" />
                <p className="font-heading font-bold text-lg">Konfirmasi Terkirim!</p>
                <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-sm mx-auto">Pesanan <span className="font-mono-data text-accent">{order.order_id}</span> sedang <span className="text-amber font-semibold">menunggu verifikasi admin</span>. Membership akan aktif otomatis setelah pembayaran Anda dicek. Terima kasih!</p>
                <button onClick={onClose} className="mt-5 h-10 px-6 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]">Selesai</button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div className="rounded-lg bg-[var(--accent)]/8 border border-[var(--accent)]/25 p-4">
                  <p className="text-xs text-[var(--text-muted)]">Paket <span className="text-[var(--text-primary)] font-semibold">{order.package_name}</span> · {order.days} hari</p>
                  <p className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mt-2">Nominal Transfer (tepat, termasuk kode unik)</p>
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-extrabold text-3xl text-accent" data-testid="manual-amount">{rupiah(order.amount_total)}</p>
                    <button onClick={() => copy(order.amount_total, "Nominal")} className="p-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]" data-testid="copy-amount"><Copy size={15} /></button>
                  </div>
                  <p className="text-[11px] text-amber mt-1">Transfer <b>tepat</b> hingga digit terakhir ({String(order.amount_total % 1000).padStart(3, "0")}) agar mudah diverifikasi.</p>
                </div>

                <div className="space-y-2">
                  {[["Bank", bank.bank], ["No. Rekening", bank.account], ["Atas Nama", bank.name]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between bg-[var(--surface-base)] rounded-lg px-3 py-2.5 border border-[var(--border-subtle)]">
                      <span className="text-xs text-[var(--text-muted)]">{k}</span>
                      <span className="flex items-center gap-2 font-mono-data font-semibold text-sm">{v}
                        {k !== "Bank" && <button onClick={() => copy(v, k)} className="text-[var(--text-secondary)] hover:text-accent"><Copy size={13} /></button>}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="h-px bg-[var(--border)]" />
                <p className="text-sm font-semibold">Sudah transfer? Konfirmasi di sini:</p>
                <div>
                  <label className="text-xs text-[var(--text-secondary)] mb-1 block">Nama Pengirim (sesuai rekening pengirim)</label>
                  <input value={sender} onChange={(e) => setSender(e.target.value)} data-testid="manual-sender" placeholder="mis. Budi Santoso" className="w-full h-10 px-3 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--accent)]" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-secondary)] mb-1 block">Catatan (opsional)</label>
                  <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="manual-note" placeholder="mis. transfer via BRImo jam 10:15" className="w-full h-10 px-3 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--accent)]" />
                </div>
                <button onClick={submit} disabled={submitting} data-testid="manual-submit" className="w-full h-11 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)] disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Mengirim…</> : "Saya Sudah Transfer"}
                </button>
                <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-1.5"><Clock size={12} /> Order ID: <span className="font-mono-data">{order.order_id}</span> · admin verifikasi manual, membership aktif setelah disetujui.</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
